import {useState, useRef, useEffect} from 'react';
import cx from 'classnames';
import sdk from '@/sdk';
import toast, {ToastType} from '@/components/Toast';

import {EDITOR_MODE} from '@/Constant';
import {isSafari} from '@/config/env';
import {getMorphoService, serviceNames} from '@/rte/editor';
import KuAuthority from '@/auth/KuAuthority';
import Popover from '@/components/Popover';
import TopSignIcon from '@/assets/svg/material/topSign.svg?tsx';
import MaterialGrabImg from '@/assets/img/material-grab.png';

import CardContent from './CardContent';
import CardTool from './CardTool';
import {IListItem, MaterialTab} from '../../interface';
import {getDragImageOffset, getContentCustomStyle} from '../../utils';
import {Status} from '../../../DocPreview/NodePreview';

import styles from './index.module.scss';

// 卡片宽高设置
const CardStyle = {
    Width: 176,
    Height: 96,
};

const OldCardStyle = {
    Width: 356,
    Height: 195,
};

interface IProps {
    data: IListItem;
    isOfficial: boolean;
    documentMode: EDITOR_MODE;
    onPreview: (id:string, content: any[]) => void;
    onTop: (data: IListItem) => void;
    onDelete: (id: string) => void;
    onEdit: (data: IListItem) => void;
    isHightLight?: boolean; // 是否需要高亮定位
    resetHightLightId?: () => void; // 重置高亮定位 id
    selectTab: MaterialTab;
}

export const MaterialMap = new WeakMap();

const Card: React.FC<IProps> = ({
    data,
    isOfficial,
    documentMode,
    onPreview,
    onTop,
    onDelete,
    onEdit,
    isHightLight = false,
    resetHightLightId,
    selectTab,
}) => {
    const [contentStatus, setContentStatus] = useState<Status>(Status.Success);
    const [isHover, setIsHover] = useState(false);
    const [isSmallImage, setIsSmallImage] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const {content, id, isTop, url, isNew, docGuid, name} = data;

    const docAuth = KuAuthority.getAuthDocument();
    const editable = docAuth.isMember;

    const isAllowDrag =
        contentStatus === Status.Success
        && [EDITOR_MODE.edit, EDITOR_MODE.meeting].includes(documentMode)
        && editable;

    const isShowHoverLayer = isHightLight || (isHover && isAllowDrag);
    const customStyle = getContentCustomStyle(content);

    const handleDragStart = async (event: React.DragEvent<HTMLDivElement>) => {
        // 自定义拖拽透明半透明图
        // 若 url 存在，即使用占位图，则 safari 场景不进行特殊处理
        if (contentRef.current && isSafari && !url) {
            // safari 下拖拽预览图 overflow 部分异常，特殊处理
            const cloneNode = contentRef.current.cloneNode(true) as HTMLDivElement;

            const width = CardStyle.Width;
            const height = CardStyle.Height;

            Object.assign(cloneNode.style, {
                width: `${width}px`,
                maxHeight: `${height}px`,
                position: 'absolute',
                left: '-9999px',
            });
            document.body.appendChild(cloneNode);

            const {offsetX, offsetY} = getDragImageOffset(event, contentRef.current);
            event.dataTransfer.setDragImage(cloneNode, offsetX, offsetY);

            setTimeout(() => {
                document.body.removeChild(cloneNode);
            }, 2000);
        } else if (contentRef.current) {
            const {offsetX, offsetY} = getDragImageOffset(event, contentRef.current);

            event.dataTransfer.setDragImage(contentRef.current, offsetX, offsetY);
        }

        const materialConfig = getMorphoService(serviceNames.IMaterialService);

        if (materialConfig && materialConfig.setMaterialToDragDataTransfer) {
            materialConfig.setMaterialToDragDataTransfer(event, content, {
                id,
                source: selectTab,
                name,
            });
        } else {
            const jsonString = JSON.stringify(content);
            event.dataTransfer.setData('application/x-morpho-slate-fragment', jsonString);
        }
    };

    useEffect(() => {
        if (isHightLight) {
            cardRef.current?.scrollIntoView({block: 'center'});
            setTimeout(() => {
                resetHightLightId && resetHightLightId();
            }, 1000);
        }
    }, [isHightLight, resetHightLightId]);

    useEffect(() => {
        if (imgRef.current) {
            imgRef.current.addEventListener('load', () => {
                // 判断图片是否小于卡片高度，若小于则隐藏圆角，需减去圆角的 8 像素
                if (imgRef.current?.clientHeight && imgRef.current?.clientHeight < (CardStyle.Height - 8)) {
                    setIsSmallImage(true);
                }
            });
        }
    }, []);

    const handleClickCard = () => {
        if (documentMode === EDITOR_MODE.preview) {
            toast({content: '请在编辑态使用', type: ToastType.Info});
        }
    };

    useEffect(() => {
        if (cardRef.current) {
            MaterialMap.set(cardRef.current, {
                content,
                dragEvent: cardRef.current,
                dragImg: contentRef.current,
                id,
                source: selectTab,
                name,
            });
        }
    }, [content, cardRef, contentRef, id, selectTab, name]);

    return (
        <Popover
            className={styles.popover}
            type={'hover'}
            placement={'top'}
            withArrow={false}
            mouseEnterDelay={200}
            beforeChange={() => isAllowDrag}
            content={(
                <div className={styles.dragTip}>
                    <img src={MaterialGrabImg} alt="" />
                    <span>拖动以插入</span>
                </div>
            )}
        >
            <div
                ref={cardRef}
                className={cx(
                    styles.container,
                    !isAllowDrag && styles.noDrag,
                    'dragEvent'
                )}
                data-morpho-draggable
                // draggable={isAllowDrag}
                // onDragStart={handleDragStart}
                onMouseEnter={() => !isHover && setIsHover(true)}
                onMouseLeave={() => isHover && setIsHover(false)}
                style={customStyle}
                onClick={handleClickCard}
            >
                {isNew && isOfficial && <div className={styles.new}>NEW</div>}
                {/* 非我的保存tab下，都可以显示封面图 */}
                {url && selectTab !== MaterialTab.Mine
                    ? (
                        <div
                            ref={contentRef}
                            className={cx(styles.imgWrapper)}
                        >
                            <img
                                ref={imgRef}
                                className={cx(
                                    styles.customImg,
                                    isSmallImage && styles.hideBorderRadius,
                                )}
                                src={url}
                                alt=""
                            />
                        </div>
                    )
                    : (
                        <CardContent
                            id={id}
                            content={content}
                            contentRef={contentRef}
                            contentStatus={contentStatus}
                            onContentStatusChange={setContentStatus}
                        />
                    )
                }
                {isTop && selectTab === MaterialTab.Mine && <TopSignIcon className={styles.topIcon} />}
                {isShowHoverLayer && (
                    <div className={styles.hoverLayer} />
                )}
                {isHover && (
                    <CardTool
                        isOfficial={isOfficial}
                        data={data}
                        onPreview={onPreview}
                        onTop={onTop}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        setIsHover={setIsHover}
                        documentMode={documentMode}
                        selectTab={selectTab}
                    />
                )}
            </div>
        </Popover>
    );
};

export default Card;
